import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Home, Briefcase, Percent, Check, Star } from 'lucide-react';
import { screenService, type CustomerProfileScreen, type CustomerNotification } from '@kss/api';
import { formatNumber } from '@kss/currency';
import { useAuth, logout } from '@kss/auth';
import { Card, Badge, Button } from '@kss/ui';

const accountNav = [
  { label: 'Profile & addresses', active: true },
  { label: 'Orders' },
  { label: 'Wallet & rewards', to: '/rewards' },
  { label: 'Notifications' },
  { label: 'Reservations' }
];

export default function AccountProfile() {
  const [data, setData] = useState<CustomerProfileScreen | null>(null);
  const { user } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Had no .catch, so a failure left the customer on "Loading account…" indefinitely.
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    screenService
      .getCustomerProfile()
      .then(setData)
      .catch(() => setFailed(true));
  }, []);

  if (!data) {
    return (
      <div className='py-24 text-center text-sm text-content-muted'>
        {failed ? 'We couldn’t load your account. Check your connection and try again.' : 'Loading account…'}
      </div>
    );
  }

  const unreadCount = data.notifications.filter((n) => n.unread).length;

  // Prefer the signed-in user's details; fall back to the mock profile.
  const profile = {
    name: user?.name ?? data.name,
    initials: user?.initials ?? data.initials,
    phone: user?.phone ?? data.phone,
    email: user?.email ?? data.email,
    tier: user?.tier ?? data.tier,
    points: user?.points ?? data.points
  };

  function signOut() {
    dispatch(logout('user-signout'));
    navigate('/', { replace: true });
  }

  return (
    <div className='mx-auto flex w-full max-w-[1200px] flex-col gap-4 p-4 md:flex-row md:px-6'>
      {/* Account nav */}
      <aside className='md:w-[210px] md:flex-shrink-0'>
        <Card className='p-3'>
          <div className='flex items-center gap-2.5 px-2.5 pb-3.5 pt-1'>
            <div className='flex h-[38px] w-[38px] items-center justify-center rounded-xl bg-[image:var(--gradient-primary)] font-display text-[13px] font-extrabold text-white'>
              {profile.initials}
            </div>
            <div>
              <div className='font-display text-[13px] font-bold'>{profile.name}</div>
              <div className='text-[10px] text-content-muted'>
                {profile.tier} · {formatNumber(profile.points)} pts
              </div>
            </div>
          </div>
          {accountNav.map((n) => (
            <div
              key={n.label}
              onClick={() => n.to && navigate(n.to)}
              className={
                'flex items-center justify-between rounded-lg px-3 py-2.5 text-[12.5px] font-medium ' +
                (n.active
                  ? 'bg-primary-light font-bold text-primary'
                  : 'cursor-pointer text-content-secondary hover:bg-surface-card-hover')
              }
            >
              {n.label}
              {n.label === 'Notifications' && unreadCount > 0 && <Badge tone='primary'>{unreadCount}</Badge>}
            </div>
          ))}
          <div
            onClick={signOut}
            className='mt-2 cursor-pointer border-t border-border px-3 pt-3 text-[12.5px] font-bold text-danger'
          >
            Logout
          </div>
        </Card>
      </aside>

      {/* Main */}
      <section className='min-w-0 flex-1'>
        {/* Profile card */}
        <Card className='flex items-center gap-3.5 p-4'>
          <div className='flex h-[50px] w-[50px] flex-shrink-0 items-center justify-center rounded-xl bg-[image:var(--gradient-primary)] font-display text-[17px] font-extrabold text-white'>
            {profile.initials}
          </div>
          <div className='min-w-0 flex-1'>
            <div className='font-display text-[15px] font-bold'>{profile.name}</div>
            <div className='mt-0.5 text-[11.5px] text-content-secondary'>
              {profile.phone} · {profile.email} · {data.lang}
            </div>
          </div>
          <Button variant='outline' size='sm'>
            Edit profile
          </Button>
        </Card>

        {/* Saved addresses */}
        <SectionLabel title='Saved addresses' action='+ Add new' />
        <div className='grid grid-cols-1 gap-2.5 sm:grid-cols-2'>
          {data.addresses.map((a) => (
            <Card key={a.id} className={'p-3.5 ' + (a.isDefault ? 'border-[1.6px] border-primary' : '')}>
              <div className='flex items-center justify-between'>
                <span className='flex items-center gap-2'>
                  <span className='flex h-[26px] w-[26px] items-center justify-center rounded-lg bg-primary-light text-primary'>
                    {a.icon === 'work' ? <Briefcase size={13} /> : <Home size={13} />}
                  </span>
                  <b className='font-display text-[12.5px]'>{a.label}</b>
                </span>
                {a.isDefault ? (
                  <Badge tone='primary'>Default</Badge>
                ) : (
                  <span className='text-[10px] font-semibold text-content-muted'>Set default</span>
                )}
              </div>
              <div className='mt-2 text-[11px] leading-relaxed text-content-secondary'>{a.line}</div>
              <div className='mt-2.5 flex gap-2.5 text-[10.5px] font-bold text-content-secondary'>
                <span className='cursor-pointer'>Edit</span>
                <span className='cursor-pointer'>Delete</span>
              </div>
            </Card>
          ))}
        </div>

        {/* Notifications */}
        <SectionLabel title='Notifications · today' action='Mark all read' />
        <Card className='overflow-hidden'>
          {data.notifications.map((n, idx) => (
            <NotificationRow key={n.id} n={n} last={idx === data.notifications.length - 1} />
          ))}
        </Card>
      </section>
    </div>
  );
}

function SectionLabel({ title, action }: { title: string; action: string }) {
  return (
    <div className='mb-2.5 mt-4 flex items-center justify-between px-0.5'>
      <span className='text-[10.5px] font-bold uppercase tracking-wider text-content-muted'>
        {title}
      </span>
      <span className='cursor-pointer font-display text-[11.5px] font-bold text-primary'>{action}</span>
    </div>
  );
}

function NotificationRow({ n, last }: { n: CustomerNotification; last: boolean }) {
  const icon =
    n.kind === 'offer' ? (
      <span className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-primary-light text-primary'>
        <Percent size={15} />
      </span>
    ) : n.kind === 'order' ? (
      <span className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-veg-light text-veg'>
        <Check size={15} />
      </span>
    ) : (
      <span className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-warning-light text-warning'>
        <Star size={14} />
      </span>
    );

  return (
    <div className={'flex items-center gap-3 px-3.5 py-2.5 ' + (last ? '' : 'border-b border-border')}>
      {icon}
      <div className='min-w-0 flex-1'>
        <b className='font-display text-xs'>{n.title}</b>
        <div className='text-[10.5px] text-content-secondary'>{n.body}</div>
      </div>
      {n.kind === 'offer' && <Badge tone='primary'>Order now</Badge>}
      {n.kind === 'reward' && n.at && (
        <span className='text-[9.5px] text-content-muted'>{n.at}</span>
      )}
      {n.unread && <span className='h-1.5 w-1.5 rounded-full bg-primary' />}
    </div>
  );
}
